import { Box, Skeleton, Typography } from "@mui/material";
import theme from "lib/theme";
import useCurrentUser from "hooks/users/useCurrentUser";
import useHome from "hooks/home/useHome";
import QuickInsights from "./QuickInsights";
import QuickStats from "./QuickStats";
import BannerCarousel from "./BannerCarousel";
import EnrolledAssets from "./EnrolledAssets";
import EnrolledAssetsV2 from "./EnrolledAssetsV2";
import { useEffect, useMemo, useState } from "react";
import usePickups from "hooks/pickups/usePickups";
import InitialReportReadyBanner from "./InitialReportReadyBanner";
import { Pickup } from "types/pickup";
import useFeatureFlag from "hooks/useFeatureFlag";
import ImageAccouncementBanner from "components/common/ImageAccouncementBanner";

export default function Home() {
  const { currentUser } = useCurrentUser();
  const { homeInfo } = useHome();
  const { getPickupsWithFilters } = usePickups();
  const [initialReportReadyPickups, setInitialReportReadyPickups] = useState<Pickup[]>([]);
  const { enabled: enrollmentV2Enabled } = useFeatureFlag("enrollment_v2");
  const hours = Number(process.env.REACT_APP_HOURS_PUBLISHED_AFTER_WINDOW_LIMIT || 72);

  const canadaBanner = (
    <ImageAccouncementBanner
      imageUrl="/announcements/canada_banner.png"
      featureFlagName="canada_banner_enabled"
      timesToDisappear={0}
      hideCloseButton={true}
      showUntil={new Date("2026-08-18")}
      redirectTo="/retrievals/send-boxes/new"
    />
  );

  useEffect(() => {
    const publishedAfter = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    getPickupsWithFilters(["state,is,initial_report_ready", `initial_report_published_at,>,${publishedAfter}`]).then(
      (response) => {
        setInitialReportReadyPickups(response.data.pickups || []);
      }
    );
  }, [getPickupsWithFilters]);

  const banners = useMemo(() => {
    const result: React.ReactNode[] = [];

    if (initialReportReadyPickups.length > 0) {
      initialReportReadyPickups.forEach((pickup) => {
        result.push(<InitialReportReadyBanner pickup={pickup} />);
      });
    }

    if (homeInfo?.enrolledAssetCount) {
      result.push(<EnrolledAssetsV2 enrolledAssetsCount={homeInfo.enrolledAssetCount} />);
    }
    return result;
  }, [homeInfo?.enrolledAssetCount, initialReportReadyPickups]);

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold">
        {!currentUser.firstName && <Skeleton variant="text" height={40} width={300} />}
        {currentUser.firstName && `Hi there, ${currentUser.firstName}!`}
      </Typography>

      <Box
        sx={{
          mt: 4,
          mb: 4,
          [theme.breakpoints.down("sm")]: {
            mt: 2,
            mb: 2,
          },
        }}
      >
        {canadaBanner}
        {enrollmentV2Enabled ? (
          <BannerCarousel banners={banners} />
        ) : homeInfo?.enrolledAssetCount ? (
          <EnrolledAssets enrolledAssetsCount={homeInfo?.enrolledAssetCount} />
        ) : null}
      </Box>

      <Box mt={2}>
        <QuickInsights homeInfo={homeInfo} />
      </Box>

      <Box
        sx={{
          mt: 4,
          [theme.breakpoints.down("sm")]: {
            mt: 2,
          },
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Quick stats
        </Typography>

        <Typography variant="body1" mt={1}>
          Take a quick look at some insightful summaries and stats that we pulled together just for you.
        </Typography>

        <Box mt={2}>
          <QuickStats homeInfo={homeInfo} />
        </Box>
      </Box>
    </Box>
  );
}
